// src/components/RutinaDashboard.js
import React, { useEffect, useState, useRef } from 'react';
import {
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  useMediaQuery,
  useTheme,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button // Agregamos Button para abrir/cerrar el calendario
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';

// Importaciones para react-day-picker
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css'; // Importa los estilos básicos de react-day-picker
import { format, parseISO } from 'date-fns'; // Para formatear y parsear fechas
import { es } from 'date-fns/locale'; // Para el idioma español

import {
  StyledMainContainer,
  StyledHeaderWrapper,
  StyledPageTitle,
  StyledTableContainer,
  StyledTableHeaderCell,
  StyledTableLinkCell,
  StyledNoUsersCell,
  StyledPaper,
  StyledActionsBar
} from './StyleComponents';

import { useNavigate } from 'react-router-dom';
import Header from './Header';
import { CircularProgress } from '@mui/material';


import api from './api'; // Asegúrate de tener configurado Axios con baseURL

// Crea un objeto Date en la zona horaria local a las 00:00:00
const createLocalDate = (dateInput) => {
  if (!dateInput) return null;

  if (typeof dateInput === 'string') {

    const date = new Date(dateInput);
    date.setUTCHours(0, 0, 0, 0);

    return date.toISOString();
  } else {

    const date = new Date(dateInput); // Crea un objeto Date desde el string ISO 8601
    date.setUTCHours(0, 0, 0, 0); // Establece las horas, minutos, segundos y milisegundos a 0 en UTC

    return date.toISOString();
  }


};




// --- Helpers ---

// Timeout duro que además aborta la(s) request(s)
const fetchWithTimeout = (promise, ms, controller) =>
  Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => {
        try { controller?.abort?.(); } catch {}
        reject(new Error("timeout"));
      }, ms)
    ),
  ]);

// Adapta la respuesta de /rutinas/fast a tu shape de tabla
const adaptarRutinasLig = (data = []) =>
  data.map((r) => {
    const created = r.created_at || "";
    const fecha = typeof created === "string" ? created.slice(0, 10) : "";
    return {
      id: r.id,
      name: r.nombre,
      determinante: r?.planta?.determinante || "---",
      planta: r?.planta?.nombre || "---",
      planta_id: r.planta_id || null,
      tipo_ejecutor: r?.tipo_rutina?.ejecutor || "",
      tipo: r?.tipo_rutina?.nombre || r?.tipo_rutina_id,
      coordinador: r?.coordinador?.nombre || "---",
      supervisor: r?.supervisor?.nombre || "---",
      status: r.status, // "pendiente" | "respondida"
      respondida: r.status === "respondida" ? "Respondida" : "Pendiente",
      semaforo: r.status === "respondida" ? r.semaforo : "",
      fecha_respuesta: fecha,
      creationDate: "",
      updateDate: "",
      cliente: r?.cliente?.nombre || "--",
    };
  });

async function loadAdminRutinasFast({
  pageSize = 250,
  parallel = 4,
  hardTimeoutMs = 30000,
  baseQuery = "",          // ej: "&status=pendiente&desde=2025-09-01&hasta=2025-09-21"
  onPartial,               // (adaptadas) => void
  maxBatches = 3,
}) {
  const controller = new AbortController();
  let batches = 0;
  const aggregated = [];
  const seen = new Set(); // evita duplicados por si el orden solapa

  const runner = (async () => {
    let nextSkip = 0;
    let keep = true;

    while (keep) {
      const skips = Array.from({ length: parallel }, (_, i) => nextSkip + i * pageSize);

      const results = await Promise.allSettled(
        skips.map((s) =>
          api.get(`/rutinas/fast?skip=${s}&limit=${pageSize}${baseQuery}`, {
            signal: controller.signal,
          })
        )
      );

      let anyFulfilled = false;
      let anyFull = false;
      let partialChunk = []; // para preview

      for (const r of results) {
        if (r.status === "fulfilled") {
          anyFulfilled = true;
          const data = r.value?.data || [];
          if (Array.isArray(data) && data.length) {
            if (data.length >= pageSize) anyFull = true;

            // Acumula crudo evitando duplicados
            for (const item of data) {
              if (!seen.has(item.id)) {
                seen.add(item.id);
                aggregated.push(item);
                partialChunk.push(item);
              }
            }
          }
        }
      }

      // Previews: pinta lo recién agregado
      if (partialChunk.length && typeof onPartial === "function") {
        try { onPartial(adaptarRutinasLig(partialChunk)); } catch {}
      }
      
      batches += 1;
      if (!anyFulfilled) break; // todo falló
      if (!anyFull) break;      // nadie trajo página completa: no hay más
      if (batches >= maxBatches) break; 
      nextSkip += pageSize * parallel;
    }
  })();

  try {
    await fetchWithTimeout(runner, hardTimeoutMs, controller);
  } catch (e) {
    // si fue timeout, seguimos con lo acumulado; no tratamos como error
  }
  return aggregated;
}


function calcularDistancia(lat1, lon1, lat2, lon2) {
  const R = 6371; // radio de la Tierra en km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c * 1000; // metros
}



function RutinasDashboard() {
  const [searchTerm, setSearchTerm] = useState('');
  const [userRole, setUserRole] = useState('user');
  const [rutinas, setRutinas] = useState([]);
  const [error, setError] = useState(null);
  // Para evitar duplicados entre lotes
  const idSetRef = useRef(new Set());
  // Para poder abortar al desmontar o al cambiar de vista
  const loaderAbortRef = useRef(null);

  // --- NUEVOS ESTADOS PARA FILTROS ---
  // dateRange ahora contendrá un objeto con 'from' y 'to' para react-day-picker
  // Inicializamos from y to como undefined explícitamente.
  const [dateRange, setDateRange] = useState({ from: undefined, to: undefined });
  const [selectedClient, setSelectedClient] = useState(''); // ID del cliente seleccionado
  const [clients, setClients] = useState([]); // Lista de clientes para el filtro
  const [isCalendarOpen, setIsCalendarOpen] = useState(false); // Estado para controlar la visibilidad del calendario
  // ------------------------------------

  // --- Roles que no deben ver los filtros de fecha y cliente ---
  const rolesResp = ['tecnico', 'operator'];

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));



// --- GEOLOCALIZACIÓN ---
const [userPosition, setUserPosition] = useState(null);

useEffect(() => {
  if ("geolocation" in navigator) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setUserPosition({ lat: latitude, lng: longitude });
        console.log("📍 Ubicación actual:", latitude, longitude);
      },
      (err) => {
        console.warn("⚠️ No se pudo obtener ubicación:", err.message);
        alert("Debes permitir el acceso a tu ubicación para usar esta función.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  } else {
    alert("Tu dispositivo no soporta geolocalización.");
  }
}, []);



  useEffect(() => {

    const storedRole = localStorage.getItem('userRole');
    if (storedRole) {
      setUserRole(storedRole);
    } else {
      localStorage.setItem('userRole', 'operador');
    }

    api.get('/clientes/?skip=0&limit=1000')
      .then((res) => {
        setClients(res.data);
      })
      .catch((err) => {
        console.error('Error al cargar clientes:', err);
        setClients([]);
      });
  }, []);

  const [userPlantas, setUserPlantas] = useState([]);
  //console.log('🧠 Filtrando rutinas...');
  //console.log('userRole:', userRole);
  //console.log('userPlantas:', userPlantas);
  //console.log('rutinas (raw):', rutinas);
  const filteredRutinas = rutinas.filter(rutina => {
    const term = searchTerm.toLowerCase();

    const matchesSearchTerm =
      rutina.name.toLowerCase().includes(term) ||
      (String(rutina.determinante)?.toLowerCase() || '').includes(term) ||
      rutina.planta.toLowerCase().includes(term) ||
      rutina.supervisor.toLowerCase().includes(term) ||
      rutina.coordinador.toLowerCase().includes(term) ||
      rutina.tipo.toLowerCase().includes(term) ||
      (rutina.respondida.toLowerCase()).includes(term) ||
      (rutina.status ? 'activo' : 'inactivo').includes(term);

    // Filtro por rango de fechas usando react-day-picker
    // Aseguramos que from y to sean siempre accedidos de un objeto, incluso si son undefined.
    const { from: startDatePicker, to: endDatePicker } = dateRange || {}; // Añadimos || {} para seguridad

    const normalizedStartDate = startDatePicker ? createLocalDate(startDatePicker) : null;
    const normalizedEndDate = endDatePicker ? createLocalDate(endDatePicker) : null;

    const routineDateNormalized = rutina.fecha_respuesta ? createLocalDate(rutina.fecha_respuesta) : null;

    // La lógica de comparación es correcta si las fechas están normalizadas a objetos Date.
    const matchesDateRange = (!normalizedStartDate || (routineDateNormalized && routineDateNormalized >= normalizedStartDate)) &&
      (!normalizedEndDate || (routineDateNormalized && routineDateNormalized <= normalizedEndDate));
    // --------------------------------------------------------

    // Filtro por cliente
    //const matchesClient = !selectedClient || String(rutina.cliente_id) === String(selectedClient);
    const matchesByRoleAndPlanta = userRole === 'admin' ||
      (userRole === 'operator' && rutina.tipo_ejecutor === userRole && userPlantas.includes(rutina.planta_id)) ||
      (userRole === 'tecnico' && rutina.tipo_ejecutor === userRole && userPlantas.includes(rutina.planta_id)) ||
      ((userRole === 'supervisor' || userRole === 'coordinador') && userPlantas.includes(rutina.planta_id));

    return matchesSearchTerm && matchesDateRange && matchesByRoleAndPlanta;
  });

  const rutinasParaMostrar = filteredRutinas/*isMobile
    ? filteredRutinas.filter(r => r.respondida !== 'Respondida')
    : filteredRutinas;*/

 const handleNavigateToRutinaDetail = (rutina) => {
  // ADMIN siempre accede
  if (userRole === 'admin') {
    navigate(`/rutina_detail/${rutina.id}`);
    return;
  }

  // Si el usuario va a responder una rutina pendiente
  if (
    (userRole === 'operator' || userRole === 'tecnico') &&
    rutina.tipo_ejecutor === userRole &&
    rutina.respondida === 'Pendiente'
  ) {
    const latPlanta = rutina.direccion_planta?.latitude;
    const lonPlanta = rutina.direccion_planta?.longitud;
    const radioPlanta = rutina.direccion_planta?.radio ?? 0;

    console.log(latPlanta)
    console.log(lonPlanta)
    console.log("Dasdadasda aqui checar que anda pasandon")
    console.log(rutina)
    console.log("toda la rtuian")

    if (!latPlanta || !lonPlanta || !radioPlanta) {
      alert("⚠️ Esta planta no tiene datos de ubicación configurados.");
      return;
    }

    if (!userPosition) {
      alert("⚠️ No se pudo obtener tu ubicación actual. Activa el GPS.");
      return;
    }

    const distancia = calcularDistancia(
      userPosition.lat,
      userPosition.lng,
      latPlanta,
      lonPlanta
    );

    console.log(`📏 Distancia al punto: ${distancia.toFixed(2)} m`);
    console.log(`🎯 Radio permitido: ${radioPlanta} m`);

    if (distancia <= radioPlanta) {
      navigate(`/responder_rutina/${rutina.id}`);
    } else {
      alert(
        `🚫 No puedes responder esta rutina. Estás a ${distancia.toFixed(
          2
        )} m del punto, fuera del radio permitido (${radioPlanta} m).`
      );
    }
    return;
  }

  if (userRole === 'supervisor' || userRole === 'coordinador') {
      if (rutina.tipo_ejecutor === userRole && rutina.respondida === 'Pendiente') {
        navigate(`/responder_rutina/${rutina.id}`);
      } else {
        if (rutina.tipo_ejecutor !== userRole && rutina.respondida === 'Pendiente') {
          alert(`Rutina pendiente de responder, sin info para mostrar`);
          return
        }
        navigate(`/rutina_detail/${rutina.id}`);
      }
      return; // Exit after handling supervisor/coordinador
    }





  // Si no cumple con las condiciones, solo puede ver detalle
  navigate(`/rutina_detail/${rutina.id}`);
};



  const [loading, setLoading] = useState(true); // 👈 Añade esto

  useEffect(() => {
    const fetchRutinas = async () => {
      try {
        setLoading(true);
        // 1) Quién soy
      const { data: me } = await api.get('/users/me');
      const isAdmin = String(me?.role || '').toLowerCase() === 'admin';

      // 2) Construir la llamada según rol
      let fetched = [];
      if (!isAdmin) {
        // Normaliza plantas: puede ser ['uuid', ...] o [{id:'uuid'}, ...]
        const plantaIds = [...new Set(
          (me?.plantas ?? [])
            .map(p => (typeof p === 'string' ? p : p?.id))
            .filter(Boolean)
        )];

        if (plantaIds.length === 0) {
          setRutinas([]);       // sin plantas asignadas → sin rutinas
          return;               // evita hacer el fetch “general”
        }

        const params = new URLSearchParams();
        params.set('skip', '0');
        params.set('limit', '20');
        plantaIds.forEach(id => params.append('planta_ids', id));

        // Usa la ruta que tengas en backend (aquí asumo /rutinas/)
        const { data } = await api.get(`/rutinas/user_fast?${params.toString()}`);
        fetched = data;
      } else {
        const baseQuery = ""; // déjalo vacío si filtras en UI

        // Preview parcial mientras carga:
        const idSetRef = new Set(); // local para evitar duplicados en preview
        await loadAdminRutinasFast({
          pageSize: 250,
          parallel: 5,
          hardTimeoutMs: 30000,
          baseQuery,
          onPartial: (adaptadas) => {
            setRutinas(prev => {
              const nuevos = [];
              for (const r of adaptadas) {
                if (!idSetRef.has(r.id)) {
                  idSetRef.add(r.id);
                  nuevos.push(r);
                }
              }
              return prev.concat(nuevos);
            });
          }
        }).then(allRaw => {
          fetched = allRaw; // <- ¡aquí sigues usando fetched como antes!
        });
      }

      // 3) Filtros de UI (opcional)
      let filtered = fetched;
        const rutinasAdaptadas = filtered.map(r => ({
          id: r.id,
          name: r.nombre,
          determinante: r.planta?.determinante || '---',
          planta: r.planta?.nombre || '---',
          planta_id: r.planta_id || null, // <-- añadir
          tipo_ejecutor: r.tipo_rutina?.ejecutor || '', // <-- añadir
          coordinador: r.coordinador?.nombre || '---',
          supervisor: r.supervisor?.nombre || '---',
          tipo: r.tipo_rutina?.nombre || r.tipo_rutina_id,
          semaforo: r.status === 'respondida' ? r.semaforo : '',
          status: r.status === 'pendiente',
          respondida: r.status === 'respondida' ? 'Respondida' : 'Pendiente',
          fecha_respuesta: r.created_at.slice(0, 10), // Asumiendo que r.created_at es 'YYYY-MM-DD'
          creationDate: '',
          updateDate: '',
          cliente: r.cliente.nombre || '--',
          direccion_planta: r.direccion_planta || null,
        }));

        setRutinas(rutinasAdaptadas);
      } catch (err) {
        console.error('Error cargando rutinas:', err);
      }
      finally {
        setLoading(false); // <-- termina carga
      }
    };

    fetchRutinas();
  }, [dateRange, selectedClient]); // Dependencias para re-cargar rutinas al cambiar los filtros


  useEffect(() => {
    const fetchUserPlantas = async () => {
      try {
        const res = await api.get('/users/me');
        //console.log('➡️ Usuario autenticado:', res.data); // 👈 Aquí

        setUserPlantas(res.data.plantas); // una lista de UUIDs
      } catch (err) {
        console.error('Error al obtener plantas del usuario:', err);
      }
    };

    if (userRole !== 'admin') {
      fetchUserPlantas();
    }
  }, [userRole]);


  return (
    <StyledMainContainer>
      <Header />
      <StyledHeaderWrapper>
        <StyledPageTitle variant="h4" component="h1" gutterBottom>
          Rutinas
        </StyledPageTitle>
      </StyledHeaderWrapper>

      <StyledActionsBar>
        {/* React-day-picker Integration */}

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {!rolesResp.includes(userRole) && (
            <TextField
              label="Fecha Inicio"
              variant="outlined"
              size="small"
              // Muestra la fecha 'from' formateada, si existe.
              value={dateRange?.from ? format(dateRange.from, 'dd/MM/yyyy', { locale: es }) : ''}
              onClick={() => setIsCalendarOpen(true)} // Abre el calendario al hacer click
              InputProps={{ readOnly: true }} // Hace el campo de solo lectura
            />)}

          {!rolesResp.includes(userRole) && (
            <TextField
              label="Fecha Fin"
              variant="outlined"
              size="small"
              // Muestra la fecha 'to' formateada, si existe.
              value={dateRange?.to ? format(dateRange.to, 'dd/MM/yyyy', { locale: es }) : ''}
              onClick={() => setIsCalendarOpen(true)} // Abre el calendario al hacer click
              InputProps={{ readOnly: true }} // Hace el campo de solo lectura
            />
          )}

          {isCalendarOpen && (
            <StyledPaper sx={{ position: 'absolute', zIndex: 1, mt: 10, p: 2 }}>
              <DayPicker
                mode="range"
                selected={dateRange}
                // Si el usuario selecciona un rango, el DayPicker devuelve un objeto como { from: Date, to: Date }
                // Si el usuario selecciona solo una fecha, devuelve { from: Date, to: undefined } o { from: undefined, to: Date } dependiendo del modo.
                // Aseguramos que setDateRange siempre reciba y almacene este objeto.
                onSelect={(range) => {
                  setDateRange(range || { from: undefined, to: undefined }); // Maneja el caso de selección de nada
                  // Opcional: Si quieres cerrar el calendario automáticamente después de seleccionar un rango completo
                  if (range?.from && range?.to) {
                    setIsCalendarOpen(false);
                  }
                }}
                locale={es}
                showOutsideDays // Muestra los días de meses anteriores/siguientes
              />
              <Button onClick={() => setIsCalendarOpen(false)} variant="outlined" sx={{ mt: 1 }}>
                Cerrar
              </Button>
            </StyledPaper>
          )}
        </Box>

        {!rolesResp.includes(userRole) && (
          <FormControl variant="outlined" size="small" sx={{ minWidth: 200, flexGrow: isMobile ? 1 : 0 }}>
            <InputLabel>Cliente</InputLabel>
            <Select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              label="Cliente"
            >
              <MenuItem value="">
                <em>Todos</em>
              </MenuItem>
              {clients.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <TextField
          label="Filtrar por nombre, fabricante, tipo, semaforo, estatus"
          variant="outlined"
          fullWidth
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: (
              <IconButton>
                <SearchIcon />
              </IconButton>
            ),
          }}
        />
      </StyledActionsBar>

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress />
        </Box>
      ) : (
        <StyledTableContainer component={StyledPaper} elevation={1}>
          <Table>
            <TableHead>
              <TableRow>
                {isMobile ? (
                  <>
                    <StyledTableHeaderCell>Nombre</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Planta</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Respondida</StyledTableHeaderCell>
                  </>
                ) : (
                  <>
                    <StyledTableHeaderCell>Fecha Creación</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Nombre</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Determinante</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Planta</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Cliente</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Coordinador</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Supervisor</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Tipo</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Respondida</StyledTableHeaderCell>
                    <StyledTableHeaderCell>Semaforo</StyledTableHeaderCell>
                  </>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {rutinasParaMostrar.length > 0 ? (
                rutinasParaMostrar.map((rutina) => (
                  <TableRow key={rutina.id}>
                    {isMobile ? (
                      <>
                        <StyledTableLinkCell
                          onClick={() => {
                            handleNavigateToRutinaDetail(rutina);
                          }}

                        >
                          {rutina.name}
                        </StyledTableLinkCell>

                        <TableCell>{rutina.planta}</TableCell>
                        <TableCell>{rutina.respondida}</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell>{rutina.fecha_respuesta}</TableCell>
                        <StyledTableLinkCell onClick={() => handleNavigateToRutinaDetail(rutina)}>
                          {rutina.name}
                        </StyledTableLinkCell>
                        <TableCell>{rutina.determinante}</TableCell>
                        <TableCell>{rutina.planta}</TableCell>
                        <TableCell>{rutina.cliente}</TableCell>
                        <TableCell>{rutina.coordinador}</TableCell>
                        <TableCell>{rutina.supervisor}</TableCell>
                        <TableCell>{rutina.tipo}</TableCell>
                        <TableCell>{rutina.respondida}</TableCell>
                        <TableCell>{rutina.semaforo}</TableCell>
                      </>
                    )}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledNoUsersCell colSpan={isMobile ? 3 : 10}>No se encontraron rutinas.</StyledNoUsersCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>)}
    </StyledMainContainer>
  );
}

export default RutinasDashboard;